import { Component } from 'react';
import { connect } from 'react-redux';
import { push, replace } from 'connected-react-router';
import PropTypes from 'prop-types';

// Design System
import { scrollTo } from 'jl-design-system/utils/scroll/scrollTo';

// Config
import { ANALYTICS_VOUCHER_UNDERSPEND_MODAL_OPEN } from '@constants/actionConstants';
import appConstants from '@constants/appConstants';
import featureConstants from '@constants/featureConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import queryStrings from '@constants/queryStringConstants';
import routes from '@constants/routeConstants';
import scrollConstants from '@constants/scrollConstants';
import { initPaymentPage } from '@redux/actions/payment/orderFormActions';
import { submitOrderAndRedirect } from '@redux/actions/payment/submitOrderActions';
import {
  setCardPayment3DSecureCancelledStatus,
  setCardPaymentPrepaidCancelledStatus,
  complete3DSMethod,
} from '@redux/actions/payment/cardPaymentActions';
import { showEditBasketModal } from '@redux/actions/edit-basket/editBasketActions';
import {
  closeMoreDetailsModal,
  openMoreDetailsModal,
  setClickCollectCutOffPassed,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { triggerTransactionStatusAnalytics } from '@redux/actions/payment/paymentActions';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import { isZeroAmount } from '@utils/index';
import hasCutOffTimePassed from '@utils/collection/hasCutOffTimePassed';
import setPageTitle from '@utils/helpers/pageTitle';
import getPathWithQueryString from '@utils/helpers/url';
import { getUserIsMyJLMemberSelector } from '@redux/reducers/user/userSelector';

// Components
import Heading from '@components/heading';
import Container from '@components/container';
import ClickAndCollectCountdown from '@components/click-and-collect-countdown';
import ClickAndCollectStoreMapView from '@components/click-and-collect-store-map-view';
import { GuernseyMessage, guernseyMessageState } from '@components/guernsey-message';
import PaymentDeductions from '@components/payment-deductions/PaymentDeductions';
import OrderDetails from '@components/order-details';
import OrderSummary from '@components/order-summary';
import ThreeDSecure from '@components/payment/three-d-secure';
import ThreeDSecureMethod from '@components/payment/three-d-secure-method';
import MembershipNotification from '@components/my-john-lewis/myjl-member-notification';
import MembersWeek from '@components/my-john-lewis/members-week';
import GetBrowserData from '@components/payment/get-browser-data';
import POSCreditBasketAmendModal from '@components/pos-credit-basket-amend-modal';
import POSGetReadyModal from '@components/pos-credit/get-ready-modal';
import GooglePayInvalidAddressModal from '@components/add-payment-details/google-pay-invalid-address-modal';
import PayPalInvalidAddressModal from '@components/add-payment-details/paypal-invalid-address-modal';
import VoucherUnderspendWarningModal, {
  showVoucherUnderspendWarning,
} from '@components/voucher-underspend-warning-modal';
import EditDetailsLink from '@components/edit-details-link';
import StreamlinedPayments from '@components/streamlined-payments';
import JerseyMessage from '@components/jersey-message';
import Rewards from '@components/rewards/Rewards';
import ScrollAnchor from '@components/scroll-anchor';

// Styles
import styles from './payment.scss';
import { getHasPrefetchedPaymentDataSelector } from '@redux/reducers/delivery/deliverySelector';

export class Payment extends Component {
  static propTypes = {
    activeModalMapMarker: PropTypes.shape({}),
    alreadyLoaded: PropTypes.bool,
    applyGiftPaymentFailed: PropTypes.bool,
    closeMoreDetailsModal: PropTypes.func,
    collectFromTime: PropTypes.string,
    complete3DSMethod: PropTypes.func,
    cutOffTimeForSelectedStore: PropTypes.string,
    disabled: PropTypes.bool,
    email: PropTypes.string,
    hasPrefetchedPaymentData: PropTypes.bool,
    initPaymentPage: PropTypes.func,
    invalidGooglePayBillingAddress: PropTypes.shape({}),
    invalidPayPalBillingAddress: PropTypes.shape({}),
    is3dsecureNotSuccess: PropTypes.bool,
    isClickCollectOrder: PropTypes.bool,
    isMyJLMember: PropTypes.bool,
    isOrderProcessingPath: PropTypes.bool,
    isPosCreditBackButton: PropTypes.bool,
    isPosCreditReturn: PropTypes.bool,
    isZeroOutstandingBalance: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.object),
    locationSearch: PropTypes.string,
    openMoreDetailsModal: PropTypes.func,
    orderDetails: PropTypes.shape({}),
    orderProcessing: PropTypes.bool,
    pathname: PropTypes.string,
    push: PropTypes.func,
    replace: PropTypes.func,
    selectedCollectionPoint: PropTypes.shape({
      id: PropTypes.string,
    }),
    selectedPaymentType: PropTypes.string,
    setCardPayment3DSecureCancelledStatus: PropTypes.func,
    setCardPaymentPrepaidCancelledStatus: PropTypes.func,
    setClickCollectCutOffPassed: PropTypes.func,
    show3dSecureFrame: PropTypes.bool,
    show3dsMethodFrame: PropTypes.bool,
    showCallCentreNumberOn3DSError: PropTypes.bool,
    showEditBasketModal: PropTypes.func,
    showGuernseyMessage: PropTypes.bool,
    showPOSCreditBasketAmendModal: PropTypes.bool,
    showPOSGetReadyModal: PropTypes.bool,
    showVoucherUnderspendWarningModal: PropTypes.bool,
    storeDetailsModalOpen: PropTypes.bool,
    submitOrderAndRedirect: PropTypes.func,
    submitOrderError: PropTypes.shape({
      body: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      bodyLookup: PropTypes.string,
      code: PropTypes.string,
      isPosCreditError: PropTypes.bool,
      message: PropTypes.string,
      refreshes: PropTypes.number,
    }),
    titleText: PropTypes.string,
    triggerAnalyticsEvent: PropTypes.func,
    triggerTransactionStatusAnalytics: PropTypes.func,
  };

  static defaultProps = {
    activeModalMapMarker: undefined,
    alreadyLoaded: false,
    applyGiftPaymentFailed: false,
    closeMoreDetailsModal: window.defaultFunc,
    collectFromTime: undefined,
    complete3DSMethod: window.defaultFunc,
    cutOffTimeForSelectedStore: undefined,
    disabled: false,
    email: undefined,
    hasPrefetchedPaymentData: false,
    initPaymentPage: window.defaultFunc,
    invalidGooglePayBillingAddress: undefined,
    invalidPayPalBillingAddress: undefined,
    is3dsecureNotSuccess: false,
    isClickCollectOrder: false,
    isMyJLMember: false,
    isOrderProcessingPath: false,
    isPosCreditBackButton: false,
    isPosCreditReturn: false,
    isZeroOutstandingBalance: false,
    items: [],
    locationSearch: '',
    openMoreDetailsModal: window.defaultFunc,
    orderDetails: {},
    orderProcessing: false,
    pathname: '',
    push: window.defaultFunc,
    replace: window.defaultFunc,
    savedPaymentFormValidationError: undefined,
    selectedCollectionPoint: undefined,
    selectedPaymentType: undefined,
    setCardPayment3DSecureCancelledStatus: window.defaultFunc,
    setCardPaymentPrepaidCancelledStatus: window.defaultFunc,
    setClickCollectCutOffPassed: window.defaultFunc,
    show3dSecureFrame: false,
    show3dsMethodFrame: false,
    showCallCentreNumberOn3DSError: false,
    showEditBasketModal: window.defaultFunc,
    showGuernseyMessage: false,
    showPOSCreditBasketAmendModal: false,
    showPOSGetReadyModal: false,
    showVoucherUnderspendWarningModal: false,
    storeDetailsModalOpen: false,
    submitOrderAndRedirect: window.defaultFunc,
    submitOrderError: undefined,
    titleText: '1. Delivery option',
    triggerAnalyticsEvent: window.defaultFunc,
    triggerTransactionStatusAnalytics: window.defaultFunc,
  };

  constructor() {
    super();

    this.state = {
      sidebarVisibleResolution: false,
    };
  }

  componentDidMount() {
    const {
      alreadyLoaded,
      cutOffTimeForSelectedStore,
      setClickCollectCutOffPassed,
      submitOrderAndRedirect,
      hasPrefetchedPaymentData,
      initPaymentPage,
      isPosCreditReturn,
      isPosCreditBackButton,
      show3dSecureFrame,
      show3dsMethodFrame,
    } = this.props;

    setTimeout(() => {
      scrollTo(0);
    });

    this.windowMatchMedia = window.matchMedia(`(min-width: ${appConstants.SIDEBAR_MIN_BREAKPOINT})`);
    this.setSidebarVisibleResolution(this.windowMatchMedia.matches);
    this.windowMatchMedia.addListener(this.mediaQueryCheck);

    if (isPosCreditReturn && hasCutOffTimePassed(cutOffTimeForSelectedStore)) {
      setClickCollectCutOffPassed(true);
    }

    if (isPosCreditBackButton || isPosCreditReturn) {
      // cancel pos credit payment, using the same API as for callback flow
      submitOrderAndRedirect({ isPosCreditOrder: true });
      return;
    }

    setPageTitle('Review and pay');

    // restore 3DS challenge listener if Payment.jsx mounts and either of these values are already true
    if (show3dsMethodFrame || show3dSecureFrame) {
      window.addEventListener('message', this.on3dsMessageReceived, false);
      initPaymentPage({ getOnly: true });
      return;
    }

    if (!alreadyLoaded && !hasPrefetchedPaymentData) {
      initPaymentPage();
    }
  }

  setSidebarVisibleResolution = (bool) => {
    this.setState({ sidebarVisibleResolution: bool });
  };

  mediaQueryCheck = () => {
    this.setSidebarVisibleResolution(this.windowMatchMedia.matches);
  };

  componentDidUpdate(prevProps) {
    const { show3dSecureFrame, show3dsMethodFrame, push, showVoucherUnderspendWarningModal, triggerAnalyticsEvent } =
      this.props;

    if (!prevProps.showVoucherUnderspendWarningModal && showVoucherUnderspendWarningModal) {
      triggerAnalyticsEvent(ANALYTICS_VOUCHER_UNDERSPEND_MODAL_OPEN);
    }

    if (!prevProps.show3dsMethodFrame && show3dsMethodFrame) {
      window.addEventListener('message', this.on3dsMessageReceived, false);
      return;
    }

    if (!prevProps.show3dSecureFrame && show3dSecureFrame) {
      const path = getPathWithQueryString({ path: routes.PAYMENT, query: queryStrings['3D_SECURE'] });
      push(path);

      window.addEventListener('message', this.on3dsMessageReceived, false);
      return;
    }

    const { locationSearch, orderProcessing, is3dsecureNotSuccess, submitOrderError, selectedPaymentType } = this.props;

    const locationSearchChanged = prevProps.locationSearch !== locationSearch;
    const isPrepaidPayment = selectedPaymentType === paymentTypeConstants.PREPAID_CARD;

    if (locationSearchChanged) {
      if (orderProcessing) {
        // User navigates Back from /payment?processing TO...
        // /payment?3dsecure
        // /payment
        const navBackFromProcessing =
          prevProps.locationSearch === `?${queryStrings.SUBMITTING_ORDER}` && !locationSearch;

        if (navBackFromProcessing) {
          const path = getPathWithQueryString({ path: routes.PAYMENT, query: prevProps.locationSearch });
          // force user back to payment /payment?processing
          push(path);
        }
        return;
      }

      // navigated from /payment?3dsecure=unsuccessful to /payment?3dsecure
      if (
        prevProps.locationSearch === `?${queryStrings['3D_SECURE_UNSUCCESSFUL']}` &&
        locationSearch === `?${queryStrings['3D_SECURE']}`
      ) {
        return;
      }

      // navigated from /payment?3dsecure to /payment and there's already a submit order error
      if (prevProps.locationSearch === `?${queryStrings['3D_SECURE']}` && !locationSearch && submitOrderError) {
        return;
      }

      // User navigates Back from /payment?3dsecure TO...
      // /payment
      // /payment?3dsecure
      // /payment?3dsecure=unsuccessful
      if (
        prevProps.locationSearch === `?${queryStrings['3D_SECURE']}` &&
        (!locationSearch || is3dsecureNotSuccess || locationSearch === `?${queryStrings['3D_SECURE']}`)
      ) {
        const { setCardPayment3DSecureCancelledStatus, setCardPaymentPrepaidCancelledStatus, initPaymentPage } =
          this.props;

        if (isPrepaidPayment) {
          setCardPaymentPrepaidCancelledStatus();
        } else {
          setCardPayment3DSecureCancelledStatus();
        }

        initPaymentPage();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.on3dsMessageReceived);
    if (this.windowMatchMedia) {
      this.windowMatchMedia.removeListener(this.mediaQueryCheck);
    }
  }

  on3dsMessageReceived = (evt = {}) => {
    const { data } = evt;
    const { complete3DSMethod, replace, submitOrderAndRedirect, triggerTransactionStatusAnalytics } = this.props;

    if (typeof data === 'string') {
      const authenticationStatusParamRegex = /authenticationStatus=(\w+)/;
      const errorCodeParamRegex = /errorCode=(\w+)/;
      if (data?.includes('status=success')) {
        window.removeEventListener('message', this.on3dsMessageReceived);
        const authenticationStatus = data.match(authenticationStatusParamRegex)?.[1];
        triggerTransactionStatusAnalytics('PENDING_AUTHORISATION', { authenticationStatus });
        submitOrderAndRedirect();
      } else if (data?.includes('status=unsuccessful')) {
        window.removeEventListener('message', this.on3dsMessageReceived);
        const path = getPathWithQueryString({ path: routes.PAYMENT, query: queryStrings['3D_SECURE_UNSUCCESSFUL'] });
        const errorCode = data.match(errorCodeParamRegex)?.[1];
        const authenticationStatus = data.match(authenticationStatusParamRegex)?.[1];
        triggerTransactionStatusAnalytics('FAILED', { errorCode, authenticationStatus });
        replace(path);
      } else if (data === 'johnlewis-3ds-method=completed') {
        window.removeEventListener('message', this.on3dsMessageReceived);
        complete3DSMethod(true);
      } else if (data === 'johnlewis-3ds-method=not_completed') {
        window.removeEventListener('message', this.on3dsMessageReceived);
        complete3DSMethod(false);
      }
    }
  };

  handleOpenMoreDetailsModal = () => {
    const { selectedCollectionPoint, openMoreDetailsModal } = this.props;

    return openMoreDetailsModal({ id: selectedCollectionPoint.id, route: routes.PAYMENT });
  };

  handleShowEditBasketModalClick = () => {
    const { pathname, showEditBasketModal } = this.props;

    showEditBasketModal({ baseRoutePath: pathname });
  };

  render() {
    const {
      activeModalMapMarker,
      applyGiftPaymentFailed,
      closeMoreDetailsModal,
      collectFromTime,
      cutOffTimeForSelectedStore,
      disabled,
      email,
      invalidGooglePayBillingAddress,
      invalidPayPalBillingAddress,
      isClickCollectOrder,
      isMyJLMember,
      isOrderProcessingPath,
      isPosCreditReturn,
      isZeroOutstandingBalance,
      items,
      orderDetails,
      selectedCollectionPoint,
      show3dSecureFrame,
      show3dsMethodFrame,
      showGuernseyMessage,
      showPOSCreditBasketAmendModal,
      showPOSGetReadyModal,
      showVoucherUnderspendWarningModal,
      storeDetailsModalOpen,
      submitOrderError,
      showCallCentreNumberOn3DSError,
      titleText,
    } = this.props;

    const { sidebarVisibleResolution } = this.state;

    const doNotRender = isOrderProcessingPath || isPosCreditReturn;

    if (doNotRender) {
      return null;
    }

    const show3DSecureMethod = show3dsMethodFrame && !submitOrderError;
    const show3DSecure = show3dSecureFrame && !submitOrderError;

    return (
      <Container maxWidth="md" width="full">
        <GetBrowserData />

        {show3DSecureMethod && <ThreeDSecureMethod />}

        {show3DSecure && <ThreeDSecure showCallCentreNumberOn3DSError={showCallCentreNumberOn3DSError} />}

        <div data-testid="payment-content">
          {isMyJLMember && (
            <>
              {!sidebarVisibleResolution && <MembershipNotification />}

              {sidebarVisibleResolution && <MembersWeek sidebarIsVisible={sidebarVisibleResolution} />}
            </>
          )}

          {!sidebarVisibleResolution && (
            <aside>
              <OrderSummary collapsed items={items} showEditBasketModal={this.handleShowEditBasketModalClick} />
            </aside>
          )}

          <Container background="white" marginBottom="3" tag="section">
            <Container borderBottom="grey-20" paddingX="3" paddingY="2" relative tag="header">
              <Heading label={titleText} tag="h2" type="s" />
              <EditDetailsLink />
            </Container>

            <article>
              {isClickCollectOrder && (
                <ClickAndCollectCountdown
                  className={styles.clickCollectCountdownContainer}
                  collectFromTime={collectFromTime}
                  cutOffTime={cutOffTimeForSelectedStore}
                />
              )}

              <OrderDetails
                collectionAddressTitle="Collection address"
                collectionDetailsTitle="Collection details"
                deliveryDetailsTitle="Delivery method"
                displayAddressTitle="Delivery details"
                email={email}
                isPaymentPage
                openMoreDetailsModal={this.handleOpenMoreDetailsModal}
                orderDetails={orderDetails}
                selectedCollectionPoint={selectedCollectionPoint}
                showEditDetailsLink={false}
              />
            </article>
          </Container>

          <Container marginBottom="3" tag="section">
            <Container background="white" borderBottom="grey-20" paddingX="3" paddingY="2" relative tag="header">
              <ScrollAnchor scrollId={scrollConstants.PAYMENT_TOP} />
              <Heading label="2. Payment" tag="h2" type="s" />
            </Container>

            <article>
              <Rewards />

              <JerseyMessage />

              {showGuernseyMessage && <GuernseyMessage />}

              <PaymentDeductions
                applyGiftPaymentFailed={applyGiftPaymentFailed}
                disabled={disabled}
                renderingContext="payment"
              />

              <StreamlinedPayments
                disabled={disabled}
                isZeroOutstandingBalance={isZeroOutstandingBalance}
                resetAfterError={submitOrderError?.refreshes}
              />

              {invalidGooglePayBillingAddress && <GooglePayInvalidAddressModal />}

              {invalidPayPalBillingAddress && <PayPalInvalidAddressModal />}

              {storeDetailsModalOpen && (
                <ClickAndCollectStoreMapView
                  activeMarker={activeModalMapMarker}
                  hideTimeAndPrice
                  onClose={closeMoreDetailsModal}
                />
              )}

              {showPOSGetReadyModal && <POSGetReadyModal />}

              {showPOSCreditBasketAmendModal && <POSCreditBasketAmendModal />}

              {showVoucherUnderspendWarningModal && <VoucherUnderspendWarningModal />}
            </article>
          </Container>
        </div>
      </Container>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const location = state.router.location;
  const pathname = location?.pathname ?? '';
  const searchParams = new URLSearchParams(location?.search);
  const query = Object.fromEntries(searchParams);

  const showVoucherUnderspendWarningModal = showVoucherUnderspendWarning(state);

  return {
    applyGiftPaymentFailed: state.payment.applyGiftPaymentFailed,
    disabled:
      ownProps.disabled ||
      state.payment.paymentProcessing ||
      state.user.isPartnerDiscountApiCallActive ||
      !state.orderForm.getPaymentPageComplete ||
      state.orderForm.removeGiftOptionLoading,
    hasPrefetchedPaymentData: getHasPrefetchedPaymentDataSelector(state),
    isPosCreditReturn: pathname === routes.PAYMENT_POS_CREDIT_CALLBACK,
    isPosCreditBackButton: query[queryStrings.POS_CREDIT] === '',
    is3dsecureNotSuccess: query['3dsecure'] === 'unsuccessful',
    isOrderProcessingPath: query.processing === '',
    isZeroOutstandingBalance: isZeroAmount(state?.orderForm?.amounts?.outstandingBalance),
    items: state.orderForm.items,
    locationSearch: location?.search,
    activeModalMapMarker: state.clickAndCollect.activeModalMapMarker,
    storeDetailsModalOpen: state.clickAndCollect.storeDetailsModalOpen,
    orderDetails: state.orderForm.orderDetails,
    orderProcessing: state.app.orderProcessing,
    pathname: location?.pathname,
    savedPaymentFormValidationError: state.payment.savedPaymentFormsValidationError,
    selectedCollectionPoint: state.clickAndCollect.selectedCollectionPoint,
    show3dSecureFrame: state.payment.show3dSecureFrame,
    show3dsMethodFrame: state.payment.show3dsMethodFrame,
    submitOrderError: state.payment.submitOrderError,
    getPayPalClientTokenFailed: state.payment.getPayPalClientTokenFailed,
    getPayPalClientTokenCallActive: state.payment.getPayPalClientTokenCallActive,
    isClickCollectOrder: state.orderForm.isClickCollectOrder,
    isMyJLMember: getUserIsMyJLMemberSelector(state),
    cutOffTimeForSelectedStore: state.clickAndCollect.cutOffTimeForSelectedStore,
    collectFromTime: state.clickAndCollect.collectionDateTimeString,
    showPOSGetReadyModal: state.payment.showPOSGetReadyModal,
    invalidGooglePayBillingAddress: state.payment.invalidGooglePayBillingAddress,
    invalidPayPalBillingAddress: state.payment.invalidPayPalBillingAddress,
    email: state.user.email,
    showPOSCreditBasketAmendModal: state.orderForm.showPOSCreditBasketAmendModal,
    showVoucherUnderspendWarningModal,
    alreadyLoaded: state.payment.alreadyLoaded,
    showCallCentreNumberOn3DSError: isFeatureActive(state, featureConstants.SHOW_CALL_CENTRE_NUMBER_ON_3DS_ERROR),
    selectedPaymentType: state.payment.selectedPaymentType,
    titleText:
      state.delivery.deliveryOptions?.clickCollectAvailable || state.delivery.deliveryOptions?.collectPlusAvailable
        ? '1. Delivery option'
        : '1. Delivery',
    showGuernseyMessage: guernseyMessageState(state),
  };
}

export function mapDispatchToProps() {
  return {
    closeMoreDetailsModal,
    complete3DSMethod,
    initPaymentPage,
    openMoreDetailsModal,
    push,
    replace,
    setCardPayment3DSecureCancelledStatus,
    setCardPaymentPrepaidCancelledStatus,
    showEditBasketModal,
    submitOrderAndRedirect,
    triggerAnalyticsEvent,
    triggerTransactionStatusAnalytics,
    setClickCollectCutOffPassed,
  };
}

export default connect((state, ownProps) => mapStateToProps(state, ownProps), mapDispatchToProps())(Payment);
